import {DiceGroupResult, FormulaResult, FormulaTerm, termSubtotal} from './dice-formula';
import {NO_TURN, Quat, Vec, quatAxisAngle, quatMultiply, quatSpin, solidFaces, tipToFace} from './dice-geometry';

export type Die = {
	/** кубик из формулы, по нему цвет: d3, d4 … d100 */
	sides: number;
	/** многогранник, которым кубик кидают: d3 — это d6 с делением числа пополам, d100 — пара d10 */
	solid: number;
	/** грани d100: десятки 00–90 или единицы 0–9 */
	variant?: 'tens' | 'units';
	/** число для кубиков вроде d7, у которых многогранник не делится: его пишут на выпавшей грани */
	odd?: {face: number; value: number};
};

const SOLIDS = [4, 6, 8, 10, 12, 20];

/** каким многогранником кидать кубик: d100 — парой d10, d3 — кубиком d6, d2 — кубиком d4 */
export function dicePlan(sides: number): Die[] {
	if (sides === 100) {
		return [
			{sides, solid: 10, variant: 'tens'},
			{sides, solid: 10, variant: 'units'},
		];
	}
	return [{sides, solid: SOLIDS.find((faces) => faces >= sides && faces % sides === 0) ?? 20}];
}

/** что выпало: число читается с грани, на которой кубик остановился */
export function dieValue(die: Die, face: number): number {
	if (die.variant === 'tens') return (face - 1) * 10;
	if (die.variant === 'units') return face - 1;
	if (die.odd) return die.odd.value;
	return Math.ceil(face / (die.solid / die.sides));
}

export const DIE_SIZE = 72;
export const STEP_MS = 1000 / 60;
export const STEPS = 144; // бросок длится 2,4 секунды
const TIP_STEPS = 18; // последние 0,3 секунды кубик заваливается на грань, на которую опирался
const RESTITUTION = 0.75; // bounce energy retention

export type PhysicsDie = {
	id: string;
	die: Die;
	x: number;
	y: number;
	vx: number;
	vy: number;
	/** вращение вокруг нормали к столу, радианы в секунду */
	spin: number;
	turn: Quat;
	/** положение перед завалом на грань и сам завал */
	tipFrom: Quat;
	tip: {face: number; axis: Vec; angle: number} | null;
};

function randomTurn(): Quat {
	const [u, v, w] = [Math.random(), Math.random(), Math.random()];
	return [
		Math.sqrt(1 - u) * Math.sin(2 * Math.PI * v),
		Math.sqrt(1 - u) * Math.cos(2 * Math.PI * v),
		Math.sqrt(u) * Math.sin(2 * Math.PI * w),
		Math.sqrt(u) * Math.cos(2 * Math.PI * w),
	];
}

/** кости расталкивают друг друга: на столе они считаются кружками в размер кубика */
function collide(dice: PhysicsDie[]) {
	for (let i = 0; i < dice.length; i++) {
		for (let j = i + 1; j < dice.length; j++) {
			const a = dice[i];
			const b = dice[j];
			const distance = Math.hypot(b.x - a.x, b.y - a.y) || 0.01;
			const overlap = DIE_SIZE - distance;
			if (overlap <= 0) continue;

			const nx = (b.x - a.x) / distance;
			const ny = (b.y - a.y) / distance;
			a.x -= (nx * overlap) / 2;
			a.y -= (ny * overlap) / 2;
			b.x += (nx * overlap) / 2;
			b.y += (ny * overlap) / 2;

			const approach = (b.vx - a.vx) * nx + (b.vy - a.vy) * ny;
			if (approach > 0) continue;
			const impulse = approach * ((1 + RESTITUTION) / 2);
			a.vx += impulse * nx;
			a.vy += impulse * ny;
			b.vx -= impulse * nx;
			b.vy -= impulse * ny;
		}
	}
}

/** шаг броска фиксированной длины: бросок от одних и тех же начальных условий всегда идёт одинаково */
export function step(dice: PhysicsDie[], index: number, w: number, h: number) {
	const dt = STEP_MS / 1000;
	const friction = Math.pow(0.985, dt * 60);
	const tipping = index >= STEPS - TIP_STEPS;
	const eased = tipping ? 1 - Math.pow(1 - (index - (STEPS - TIP_STEPS) + 1) / TIP_STEPS, 3) : 0;

	for (const d of dice) {
		d.x += d.vx * dt * (1 - eased);
		d.y += d.vy * dt * (1 - eased);

		if (d.x < 0) {
			d.x = 0;
			d.vx = Math.abs(d.vx) * RESTITUTION;
		} else if (d.x > w - DIE_SIZE) {
			d.x = w - DIE_SIZE;
			d.vx = -Math.abs(d.vx) * RESTITUTION;
		}
		if (d.y < 0) {
			d.y = 0;
			d.vy = Math.abs(d.vy) * RESTITUTION;
		} else if (d.y > h - DIE_SIZE) {
			d.y = h - DIE_SIZE;
			d.vy = -Math.abs(d.vy) * RESTITUTION;
		}

		d.vx *= friction;
		d.vy *= friction;
		d.spin *= friction;

		if (tipping) {
			if (!d.tip) {
				d.tipFrom = d.turn;
				d.tip = tipToFace(d.turn, solidFaces(d.die.solid));
			}
			d.turn = quatMultiply(quatAxisAngle(d.tip.axis, d.tip.angle * eased), d.tipFrom);
		} else {
			// кубик катится по столу: ось вращения поперёк движения, плюс вращение вокруг нормали к столу
			d.turn = quatSpin(d.turn, [(-d.vy / DIE_SIZE) * 2, (d.vx / DIE_SIZE) * 2, d.spin], dt);
		}
	}

	collide(dice);
}

export function throwDice(terms: FormulaTerm[], w: number, h: number): PhysicsDie[] {
	const dice: PhysicsDie[] = [];
	terms.forEach((term, ti) => {
		if (term.type !== 'dice') return;
		for (let i = 0; i < term.count; i++) {
			dicePlan(term.sides).forEach((die, di) =>
				dice.push({
					id: `${ti}-${i}-${di}`,
					die,
					x: w * 0.1 + Math.random() * (w * 0.8 - DIE_SIZE),
					y: h * 0.1 + Math.random() * (h * 0.6 - DIE_SIZE),
					vx: (Math.random() - 0.5) * 900,
					vy: (Math.random() - 0.5) * 700,
					spin: (Math.random() < 0.5 ? 1 : -1) * (2 + Math.random() * 4),
					turn: randomTurn(),
					tipFrom: NO_TURN,
					tip: null,
				}),
			);
		}
	});
	return dice;
}

/**
 * Бросок считается до конца ещё до первого кадра: так известно, что выпало, и число можно написать
 * на грани заранее. Считается копия, а показывается тот же бросок от тех же начальных условий.
 */
export function rollOut(dice: PhysicsDie[], terms: FormulaTerm[], w: number, h: number): FormulaResult {
	const copy = dice.map((d) => ({...d, turn: [...d.turn] as Quat}));
	for (let i = 0; i < STEPS; i++) step(copy, i, w, h);

	const faces = new Map(copy.map((d) => [d.id, d.tip?.face ?? 1]));
	const groups: DiceGroupResult[] = [];
	let modifier = 0;
	let next = 0;

	for (const term of terms) {
		if (term.type === 'number') {
			modifier += term.value * term.sign;
			continue;
		}
		const values: number[] = [];
		for (let i = 0; i < term.count; i++) {
			const parts = dice.slice(next, next + (term.sides === 100 ? 2 : 1));
			next += parts.length;
			// у d7 и других кубиков, не делящих многогранник, число выпасть не может: его пишут на выпавшей грани
			for (const part of parts) {
				if (part.die.solid % part.die.sides) part.die.odd = {face: faces.get(part.id)!, value: Math.floor(Math.random() * part.die.sides) + 1};
			}
			const value = parts.reduce((sum, part) => sum + dieValue(part.die, faces.get(part.id)!), 0);
			values.push(term.sides === 100 && value === 0 ? 100 : value);
		}
		groups.push({term, values, subtotal: termSubtotal(term, values)});
	}

	return {groups, modifier, total: groups.reduce((a, g) => a + g.subtotal, 0) + modifier};
}
